import React, { useEffect, useMemo, useState, useCallback } from "react";
import { debounce } from "lodash";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  MRT_PaginationState,
} from "material-react-table";
import {
  Button,
  Chip,
  MenuItem,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  styled,
  Select,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Title from "../../../../../components/Title";
import {
  fetchUsers,
  updateUserStatus,
} from "../../../../../features/slices/Users";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../../store";
import { useNavigate } from "react-router-dom";
import Back from "../../../../../components/Back";
import FormattedDateCell from "../../../../../components/FormattedDateCell";
import { useSnackbar } from "../../../../../context/SnackbarContext";
import { useAdminResetPassword } from "../../../../../api/user/use-admin-reset-password";

const PendingChip = styled(Chip)(({ theme }) => ({
  animation: "blink 1s linear infinite",
  "@keyframes blink": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.5 },
    "100%": { opacity: 1 },
  },
  cursor: "pointer",
}));
interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  created_at: Date;
  updated_at: Date;
  user_group_id: number;
  active: boolean;
  status?: string;
  timezone?: string;
  group?: {
    id: number;
    title: string;
    permissions: string[];
  };
}

const Users = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<User[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });
  const [dialogOpenId, setDialogOpenId] = useState<number | null>(null);
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { mutate: resetPassword, isPending: resetPending } =
    useAdminResetPassword();

  const handleEditUser = (id: number) => {
    navigate(`/management/users/${id}/edit`);
  };

  const fetchData = useCallback(
    debounce(async () => {
      setIsLoading(true);
      setIsRefetching(true);
      try {
        const users = await dispatch(fetchUsers()).unwrap();
        setRowCount(users.length);
        setData(users);
      } catch (error) {
        setIsError(true);
      } finally {
        setIsLoading(false);
        setIsRefetching(false);
      }
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        accessorKey: "active",
        header: "Active",
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => (
          <Chip
            label={row.original.active ? "Active" : "Inactive"}
            sx={{
              backgroundColor: row.original.active ? "#4CAF50" : "#F44336",
              color: "#FFFFFF",
            }}
          />
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ cell, row }) => {
          const status = cell.getValue<string>();
          const email = row.original.email;
          const showDialog = dialogOpenId === row.original.id;
          const isAdminEmail = email === "dsoadmin@globallogic.com";

          let actions = [];
          if (status === "pending") {
            actions = [
              { label: "Approve", color: "success", status: "approved" },
              { label: "Decline", color: "error", status: "declined" },
            ];
          } else if (status === "approved") {
            actions = [
              { label: "Decline", color: "error", status: "declined" },
            ];
          } else if (status === "declined") {
            actions = [
              { label: "Approve", color: "success", status: "approved" },
            ];
          }

          const handleChipClick = () => {
            if (!isAdminEmail) {
              setDialogOpenId(row.original.id);
            }
          };

          return (
            <>
              <Tooltip
                title={
                  isAdminEmail
                    ? "This user cannot be modified"
                    : `${
                        status.charAt(0).toUpperCase() + status.slice(1)
                      } – Click to update status`
                }
              >
                <span>
                  {status === "pending" ? (
                    <PendingChip
                      label="Pending"
                      color="warning"
                      onClick={handleChipClick}
                      sx={{
                        pointerEvents: isAdminEmail ? "none" : "auto",
                        opacity: isAdminEmail ? 0.5 : 1,
                      }}
                    />
                  ) : (
                    <Chip
                      label={status.charAt(0).toUpperCase() + status.slice(1)}
                      color={
                        status === "approved"
                          ? "success"
                          : status === "declined"
                          ? "error"
                          : "default"
                      }
                      sx={{
                        cursor: isAdminEmail ? "not-allowed" : "pointer",
                        pointerEvents: isAdminEmail ? "none" : "auto",
                        opacity: isAdminEmail ? 0.5 : 1,
                      }}
                      onClick={handleChipClick}
                    />
                  )}
                </span>
              </Tooltip>
              {!isAdminEmail && (
                <Dialog open={showDialog} onClose={() => setDialogOpenId(null)}>
                  <DialogTitle>Change User Status</DialogTitle>
                  <DialogContent>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        mt: 2,
                        justifyContent: "center",
                      }}
                    >
                      {actions.map((a) => (
                        <Button
                          key={a.status}
                          variant="contained"
                          color={a.color}
                          onClick={async () => {
                            await dispatch(
                              updateUserStatus({
                                id: row.original.id,
                                status: a.status,
                              })
                            ).unwrap();
                            setDialogOpenId(null);
                            fetchData();
                          }}
                        >
                          {a.label}
                        </Button>
                      ))}
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setDialogOpenId(null)}>
                      Cancel
                    </Button>
                  </DialogActions>
                </Dialog>
              )}
            </>
          );
        },
      },

      {
        accessorKey: "username",
        header: "Username",
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "first_name",
        header: "First Name",
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "last_name",
        header: "Last Name",
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      // {
      //   accessorKey: "user_group_id",
      //   header: "User Group ID",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      // },
      {
        accessorKey: "user_group_title",
        header: "User Group",
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => <span>{row.original.group?.title || "N/A"}</span>,
      },
      {
        accessorKey: "timezone",
        header: "Timezone",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
    ],
    [dialogOpenId, dispatch, fetchData]
  );

  const table = useMaterialReactTable({
    columns,
    data,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.paper,
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" },
    },
    muiTableContainerProps: {
      sx: { maxHeight: "calc(78vh - 112px)" },
    },
    muiTableHeadCellProps: {
      sx: { fontWeight: 700, bgcolor: "background.default", color: "text.secondary", letterSpacing: 0.2 },
    },
    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [10, 25, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    columnFilterDisplayMode: "popover",
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "comfortable",
      showGlobalFilter: true,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
    },
    manualFiltering: false,
    manualPagination: false,
    enableGrouping: false,
    enableRowSelection: false,
    enableRowActions: true,
    enableColumnOrdering: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    positionActionsColumn: "last",
    enableFacetedValues: true,
    getRowId: (row) => String(row.id),
    layoutMode: "grid-no-grow",
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    rowCount,
    state: {
      pagination,
      columnFilters,
      globalFilter,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Tooltip arrow title="Refresh Data">
        <IconButton onClick={() => fetchData()}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    ),
    renderRowActionMenuItems: ({ row }) => [
      <MenuItem
        key={1}
        onClick={() => handleEditUser(Number(row.id))}
        sx={{ m: 0 }}
      >
        Edit
      </MenuItem>,
      <MenuItem
        key={2}
        // disabled={resetPending}
        disabled={true}
        onClick={() =>
          resetPassword(row.original.id, {
            onSuccess: () =>
              showSnackbar("Reset password email sent", "success"),
            onError: () => showSnackbar("Failed to send reset email", "error"),
          })
        }
      >
        Reset Password (Email)
      </MenuItem>,
    ],
  });

  return (
    <>
      <Title>Users Overview</Title>
      <MaterialReactTable table={table} />
      <Back />
    </>
  );
};

export default Users;
